/**
 * Knowledge Graph Entity Node Component
 *
 * Circular node visualization for Knowledge Graph entities.
 * This follows the standard KG visualization style with:
 * - Circular/ellipse shape for entities
 * - Color-coded by KG Area type
 * - Hover to see properties
 * - Click to expand details
 */

import React, { memo, useState } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import {
  Database,
  Activity,
  BarChart3,
  CircleDot,
  Star,
  ChevronDown,
  Info,
} from 'lucide-react';

interface NodeProperty {
  name: string;
  type?: string;
  isPrimaryKey?: boolean;
  description?: string;
}

interface KGEntityNodeData {
  label: string;
  category?: string; // KG Area name
  description?: string;
  properties: NodeProperty[];
  isCentral?: boolean;
  connectionCount?: number;
  colors: {
    bg: string;
    border: string;
    text: string;
  };
  // KG-First specific fields
  kgAreaId?: string;
  kgAreaName?: string;
  isMasterData?: boolean;
  isTransactional?: boolean;
  isAnalytical?: boolean;
}

// Get icon for entity type
const getEntityTypeIcon = (data: KGEntityNodeData, size: string = 'w-5 h-5') => {
  if (data.isMasterData || data.category?.includes('Hub')) {
    return <Database className={`${size} text-white`} />;
  }
  if (data.isTransactional || data.category?.includes('-to-')) {
    return <Activity className={`${size} text-white`} />;
  }
  if (data.isAnalytical || data.category?.includes('Analytics') || data.category?.includes('Metrics')) {
    return <BarChart3 className={`${size} text-white`} />;
  }
  return <CircleDot className={`${size} text-white`} />;
};

export const KGEntityNode: React.FC<NodeProps> = memo(({ data, selected }) => {
  const nodeData = data as unknown as KGEntityNodeData;
  const [showDetails, setShowDetails] = useState(false);

  // Determine node size based on centrality
  const baseSize = nodeData.isCentral ? 100 : 80;
  const fontSize = nodeData.isCentral ? 'text-sm' : 'text-xs';

  return (
    <div className="relative">
      {/* Connection handles - positioned around the circle */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-2 !h-2 !border-2 !border-white !-left-1"
        style={{ backgroundColor: nodeData.colors.border }}
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!w-2 !h-2 !border-2 !border-white !-right-1"
        style={{ backgroundColor: nodeData.colors.border }}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        className="!w-2 !h-2 !border-2 !border-white !-top-1"
        style={{ backgroundColor: nodeData.colors.border }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        className="!w-2 !h-2 !border-2 !border-white !-bottom-1"
        style={{ backgroundColor: nodeData.colors.border }}
      />

      {/* Main circular node */}
      <div
        className={`
          relative flex flex-col items-center justify-center
          rounded-full shadow-lg cursor-pointer
          transition-all duration-200 ease-in-out
          ${selected ? 'ring-4 ring-blue-400 ring-offset-2 scale-110' : 'hover:scale-105'}
        `}
        style={{
          width: baseSize,
          height: baseSize,
          backgroundColor: nodeData.colors.border,
          boxShadow: `0 4px 14px ${nodeData.colors.border}60`,
        }}
        onClick={() => setShowDetails(!showDetails)}
      >
        {/* Central star indicator */}
        {nodeData.isCentral && (
          <div className="absolute -top-1 -right-1">
            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
          </div>
        )}

        {/* Entity type icon */}
        <div className="mb-1">
          {getEntityTypeIcon(nodeData)}
        </div>

        {/* Entity name */}
        <span className={`font-bold text-white ${fontSize} text-center px-2 leading-tight`}>
          {nodeData.label}
        </span>

        {/* Property count indicator */}
        {nodeData.properties.length > 0 && (
          <span className="text-[9px] text-white/70 mt-0.5">
            {nodeData.properties.length} props
          </span>
        )}
      </div>

      {/* KG Area label below the node */}
      {nodeData.category && (
        <div
          className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap"
        >
          <span
            className="text-[10px] font-medium px-2 py-0.5 rounded-full"
            style={{
              backgroundColor: `${nodeData.colors.bg}`,
              color: nodeData.colors.text,
              border: `1px solid ${nodeData.colors.border}40`,
            }}
          >
            {nodeData.category}
          </span>
        </div>
      )}

      {/* Expandable details panel */}
      {showDetails && (
        <div
          className="absolute top-full left-1/2 transform -translate-x-1/2 mt-8 z-50
                     bg-white rounded-lg shadow-xl border border-neutral-200 p-3 min-w-[200px] max-w-[280px]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-neutral-100">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: nodeData.colors.border }}
            >
              {getEntityTypeIcon(nodeData, 'w-3 h-3')}
            </div>
            <div>
              <div className="font-semibold text-sm text-neutral-800">{nodeData.label}</div>
              {nodeData.category && (
                <div className="text-[10px] text-neutral-500">{nodeData.category}</div>
              )}
            </div>
          </div>

          {/* Description */}
          {nodeData.description && (
            <p className="text-xs text-neutral-600 mb-2">{nodeData.description}</p>
          )}

          {/* Properties */}
          {nodeData.properties.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1 font-medium">
                Properties
              </div>
              <ul className="space-y-0.5 max-h-[150px] overflow-y-auto">
                {nodeData.properties.map((prop, index) => (
                  <li
                    key={`${prop.name}-${index}`}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className={`${prop.isPrimaryKey ? 'font-semibold text-amber-700' : 'text-neutral-600'}`}>
                      {prop.isPrimaryKey && '🔑 '}
                      {prop.name}
                    </span>
                    <span className="text-neutral-400 font-mono text-[10px]">
                      {prop.type || 'String'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Close hint */}
          <div className="text-[10px] text-neutral-400 text-center mt-2 pt-2 border-t border-neutral-100">
            Click node to close
          </div>
        </div>
      )}
    </div>
  );
});

KGEntityNode.displayName = 'KGEntityNode';

export default KGEntityNode;
